/*
  Custom Validation Hook for Forms
  Config declares the field validation rule and the initial state contains the form state.
*/
import { useReducer } from 'react';
import * as _ from 'lodash';
import { type } from 'jquery';

/*
  Validation Methods
  Iterates through all of the validator property in config
  Validate single field in a form via unique fieldname
*/
const validateSingleField = (fieldName, fields, configFields, style) => {
  let errors = {};
  let validityFlag = true;
  const validators = configFields[fieldName];
  validators.forEach(validator=>{
    let isInvalid = validator( fields[fieldName].value );
    if( typeof isInvalid== 'function' ) {
      isInvalid = isInvalid(fields, fieldName);
    }
    if( isInvalid ) {
      fields[fieldName].style = style;
      errors[fieldName] = 'Error';
      validityFlag = false;
    }
  })
  return {
    fields: fields,
    flag: validityFlag,
    errors: errors
  }
}

/*
  Validate ALL fields in the form specified in the form control config.
  Put all validation error in errors prop.
*/
const validateFields = (fields, configFields, style) => {
  let errors = {};
  let validityFlag = true;
  Object.keys(fields).forEach(key=>{
    let validators = configFields[key];
    validators.forEach(validator=>{
      if( typeof(fields[key].value) === "object" && fields[key].value.length > 0){
        fields[key].value.forEach(element=>{
          let isInvalid = validator(element);
          if( typeof isInvalid == 'function' ) {
            isInvalid = isInvalid(fields, key);
          }
          if( isInvalid ) {
            fields[key].style = style;
            errors[key] = 'Error '+validator;
            validityFlag = false;
          }
        })
      } else {
        if( typeof(fields[key].value) === "object" ){ fields[key].value = '' }
        let isInvalid = validator( fields[key].value );
        if( typeof isInvalid == 'function' ) {
          isInvalid = isInvalid(fields, key);
        }
        if( isInvalid ) {
          fields[key].style = style;
          errors[key] = 'Error '+validator;
          validityFlag = false;
        }
      }
    })
  })
  return {
    fields: fields,
    flag: validityFlag,
    errors: errors
  }
}

/*
  Validate dynamic/growing fields.
*/
const validateDynamicFields = (fields, dynamicFields, configFields, style) => {
  let errors = {};
  let validityFlag = true;
  Object.keys(dynamicFields).forEach(key=>{
    const validatorGroup = configFields[key];
    for(var index in dynamicFields[key]){

      if('required' in dynamicFields[key][index]){
        console.log(dynamicFields[key][index]);
        if(dynamicFields[key][index]['required'].value === 'false'){
          continue;
        }
      }

      for(var subkey in dynamicFields[key][index]){
        dynamicFields[key][index][subkey].style = {};
        const subpPropVal = dynamicFields[key][index][subkey].value;
        const validators = validatorGroup[subkey];
        for(var vIndex in validators){
          var isInvalid = validators[vIndex](subpPropVal);
          if( typeof isInvalid == 'function' ) {
            isInvalid = isInvalid(fields, key);
          }
          if( isInvalid ) {
            errors[key] = 'Error';
            dynamicFields[key][index][subkey].style = style;
            validityFlag = false;
            break;
          }
        }
      }

    }
  })
  return {
    fields: dynamicFields,
    flag: validityFlag,
    errors: errors
  }
}

/*
  Form validation reducer for the validaiton hook.
  Listens for value change and submit actions
*/
const validationReducer = (state, action) => {
  switch(action.type) {
    // Causes reducer to sync with the form state
    case 'SUBMIT': {
      return {
        ...state,
        submitted: !state.submitted
      }
    };
    // Triggers on field change action
    case 'CHANGE': {
      const fields = { ...state.fields, ...action.payload };
      return {
        ...state,
        fields: fields,
      };
    };
    // Triggers on dynamic field chnage
    case 'CHANGE_DYNAMIC': {
      const dynamicFields = { ...state.dynamicFields, ...action.payload };
      return {
        ...state,
        dynamicFields: dynamicFields,
      };
    };
    // Triggers dynamic field Change via index
    case 'CHANGE_DYNAMIC_INDEX': {
      const { fieldName, index, key, value } = action.payload;
      state.dynamicFields[fieldName][index][key].value = value;
      return {
        ...state,
        dynamicFields: state.dynamicFields
      }
    };
    // Triggers set dynamic field action
    case 'SET_DYNAMIC': {
      const fields = { ...state.dynamicFields, ...action.payload };
      return {
        ...state,
        dynamicFields: fields
      }
    };
    // Triggers single field validation
    case 'VALIDATE_SINGLE': {
      const { fieldName, config, invalidStyle } = action.payload;
      const { fields, flag, errors } = validateSingleField( fieldName, state.fields, config.fields, invalidStyle );
      return {
        ...state,
        fieldErrors: { ...state.fieldErrors, ...errors },
        fields: fields
      }
    };
    // Validate Dynamic Fields
    case 'VALIDATE_DYNAMIC': {
      const { config, invalidStyle } = action.payload;
      const { fields, flag, errors } = validateDynamicFields( state.fields, state.dynamicFields, config.dynamicFields, invalidStyle );
      return {
        ...state,
        dynamicFieldErrors: errors,
        dynamicFields: fields,
        dynamicFieldsValid: flag
      }
    };
    // Remove item by index
    case 'SYNC': {
      const { fieldName, index, key, value } = action.payload;
      state.dynamicFields[fieldName][index][key].value = value;
      return {
        ...state,
        dynamicFields: state.dynamicFields
      }
    };
    // Triggers validation action
    case 'VALIDATE': {
      const { config, invalidStyle } = action.payload;
      const { fields, flag, errors } = validateFields( state.fields, config.fields, invalidStyle );
      return {
        ...state,
        fieldErrors: errors,
        fields: fields,
        fieldsValid: flag
      }
    }
    // Triggers form field reset
    case 'RESET': {
      const keys = Object.keys(action.payload.config.fields);
      let exceptions = [];
      if(action.payload.exceptions){exceptions = action.payload.exceptions};
      const { fields } = state;
      for(let index in keys){
        if(!exceptions.includes(keys[index])){
          fields[keys[index]].value = ''
        }
      }
      return {
        ...state,
        fields: fields
      }
    }
    default: throw new Error('Unknow Reducer Action Type');
  }
}

/*
  Initial Form State For the Reducer to consume.
  Parse Dynamic Fields and add Style
*/
const initialState = (fields) => {
  Object.keys(fields.fields).forEach(key=>{
    if(fields.fields[key].value === null){
      fields.fields[key].value = '';
    }
  });
  Object.keys(fields.dynamicFields).forEach(key=>{
    let fieldVal = fields.dynamicFields[key];
    fieldVal.forEach(element=>{
      Object.keys(element).forEach(prop=>{
        let prevVal = element[prop];
        if(typeof prevVal === 'string'){
          element[prop] = {
            value: prevVal,
            style: {}
          }
        }
        if(typeof prevVal === 'number'){
          element[prop] = {
            value: prevVal.toString(),
            style: {}
          }
        }
      })
    })
  })
  return {
    fields: fields.fields,
    dynamicFields: fields.dynamicFields,
    submitted: false,
    fieldsValid: false,
    dynamicFieldsValid: false,
    fieldErrors: {},
    dynamicFieldErrors: {}
  }
}

/*
  Current the current form state values
  Build the form state object for api calls
*/
const captureFormState = (state) => {
  let formState = {};
  Object.keys(state.fields).forEach(key=>{
    formState[key] = state.fields[key].value;
  })
  Object.keys(state.dynamicFields).forEach(key=>{
    formState[key] = [];
    for(var index in state.dynamicFields[key]) {
      var nfield = {};
      Object.keys(state.dynamicFields[key][index]).forEach(vkey=>{
        nfield[vkey] = state.dynamicFields[key][index][vkey].value;
      })
      formState[key].push(nfield);
    }
  })
  return {
    formState: formState,
    isFormValid: state.fieldsValid && state.dynamicFieldsValid,
    fieldErrors: state.fieldErrors,
    dynamicFieldErrors: state.dynamicFieldErrors
  }
}

/*
  Validation Hook
  Contains the state and dispatcher of form state for the input fields rule evaluation.
  Takes config as prop for the input field rule validation configuration and initial state
  for the form's initial stsate. Config obj should contain prop fields and onSubmitted call back function.
  Add Invalid style prop for Input style for invalid fields
*/
export const useValidation = (config, fields, invalidStyle) => {
  const [ state, dispatch ] = useReducer(validationReducer, initialState(fields));
  return {
    /* Object prop for errors */
    fieldErrors: state.fieldErrors,
    dynamicFieldErrors: state.dynamicFieldErrors,

    /* Force Set State value */
    forceSetField: (fieldName, fieldValue) => {
      if (!config.fields[fieldName]){ return; }
      dispatch({type: 'CHANGE', payload: { [fieldName]: {value: fieldValue} }});
    },

    /* Force set Dynamic Field */
    forceSetFieldDynamic: (fieldName, fieldValue) => {
      if (!config.dynamicFields[fieldName]){ return; }
      let tempObj = _.cloneDeep(fieldValue);
      tempObj.map(data=>{
        const keys = Object.keys(data);
        for(var i=0;i<keys.length;i++){
          if(typeof(data[keys[i]])!=='object'){
            data[keys[i]] = {
              style: {},
              value: data[keys[i]].toString()
            }
          } else {
            data[keys[i]] = {
              style: {},
              value: data[keys[i]]
            }
          }
        }
      });
      dispatch({type: 'CHANGE_DYNAMIC', payload: { [fieldName]: tempObj }})
    },

    /* Set Dynamic Field value */
    setDynamicField: (fieldName, fieldValue) => {
      if (!config.dynamicFields[fieldName]){ return; }
      dispatch({type: 'SET_DYNAMIC', payload: { [fieldName]:  fieldValue }});
    },

    /* Get form props for submission triggers*/
    getFormProps: () => ({
      onSubmit: e => {
        dispatch({type: 'VALIDATE', payload: {invalidStyle: invalidStyle, config: config}});
        dispatch({type: 'VALIDATE_DYNAMIC', payload: {invalidStyle: invalidStyle, config: config}});
      }
    }),

    /* Get Field props for input fields */
    getTextFieldProps: fieldName => ({
      onChange: e => {
        if (!config.fields[fieldName]){ return; }
        dispatch({type: 'CHANGE', payload: { [fieldName]: { value: e } }});
        dispatch({type: 'VALIDATE_SINGLE', payload: {fieldName: fieldName, invalidStyle: invalidStyle, config: config}});
      },
      name: fieldName,
      style: state.fields[fieldName].style,
      value: state.fields[fieldName].value
    }),

    /* Get Field props for input fields */
    getTextAreaProps: fieldName => ({
      onChange: e => {
        if (!config.fields[fieldName]){ return; }
        dispatch({type: 'CHANGE', payload: { [fieldName]: { value: e } }});
        dispatch({type: 'VALIDATE_SINGLE', payload: {fieldName: fieldName, invalidStyle: invalidStyle, config: config}});
      },
      name: fieldName,
      style: state.fields[fieldName].style,
      value: state.fields[fieldName].value
    }),

    /* Get Field props for input fields */
    getDateFieldProps: fieldName => ({
      onChange: e => {
        if (!config.fields[fieldName]){ return; }
        dispatch({type: 'CHANGE', payload: { [fieldName]: { value: e } }});
        dispatch({type: 'VALIDATE_SINGLE', payload: {fieldName: fieldName, invalidStyle: invalidStyle, config: config}});
      },
      name: fieldName,
      style: state.fields[fieldName].style,
      value: state.fields[fieldName].value
    }),

    /* Get Field props for input fields */
    getDropdownProps: fieldName => ({
      onChange: e => {
        if (!config.fields[fieldName]){ return; }
        dispatch({type: 'CHANGE', payload: { [fieldName]: {value: e.target.value} }});
      },
      name: fieldName,
      style: state.fields[fieldName].style,
      value: state.fields[fieldName].value
    }),

    getDropdownPropsDynamic: (fieldName, key, index) => ({
      onChange: e => {
        if (!config.dynamicFields[fieldName]){ return; }
        dispatch({type: 'CHANGE_DYNAMIC_INDEX', payload: { fieldName: fieldName, key: key, value: e.target.value, index: index }})
        dispatch({type: 'VALIDATE_DYNAMIC', payload: {invalidStyle: invalidStyle, config: config}});
      },
      name: fieldName,
      style: state.dynamicFields[fieldName][index][key].style,
      value: state.dynamicFields[fieldName][index][key].value
    }),

    resetForm: (exceptions) => {
      dispatch({type: 'RESET', payload: { config: config, exceptions: exceptions }})
    },

    /* Get Radio Buton props */
    getRadioButtonProps: fieldName => ({
      onChange: value => {
        if (!config.fields[fieldName]){ return; }
        dispatch({type: 'CHANGE', payload: { [fieldName]: {value: value} }});
      },
      name: fieldName,
      style: state.fields[fieldName].style,
      value: state.fields[fieldName].value
    }),

    /* Get checboxes props */
    getCheckBoxesProps: fieldName => ({
      onChange: value => {
        if (!config.fields[fieldName]){ return; }
        dispatch({type: 'CHANGE', payload: { [fieldName]: {value: value}}})
      },
      name: fieldName,
      style: state.fields[fieldName].style,
      value: state.fields[fieldName].value
    }),

    /* Get Dynamic field props for list fields */
    getTextFieldPropsDynamic: (fieldName, key, index) => ({
      onChange: e => {
        if (!config.dynamicFields[fieldName]){ return; }
        dispatch({type: 'CHANGE_DYNAMIC_INDEX', payload: { fieldName: fieldName, key: key, value: e, index: index }})
        dispatch({type: 'VALIDATE_DYNAMIC', payload: {invalidStyle: invalidStyle, config: config}});
      },
      name: key,
      style: state.dynamicFields[fieldName][index][key].style,
      value: state.dynamicFields[fieldName][index][key].value
    }),

    /* Get Form Field State Value */
    getField: fieldName => { return state.fields[fieldName].value; },

    /* Get Form Dynamic Field State Value */
    getDynamicField: fieldName => { return state.dynamicFields[fieldName] },

    /* Demand a Validation Trigger */
    validate: () => {
      state.fieldsValid = validateFields(state.fields, config.fields, invalidStyle).flag;
      state.dynamicFieldsValid = validateDynamicFields(state.fields, state.dynamicFields, config.dynamicFields, invalidStyle).flag;
      dispatch({type: 'SUBMIT'});
      return captureFormState(state);
    }
  }
}




